import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import type { PreflightPlatformCode } from "@nexus/contracts";
import { Button, EmptyState } from "@nexus/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  preflightCreativePreviewsQueryOptions,
  preflightInsightsQueryOptions,
} from "@/lib/queries";
import { tenantSettingsQueryOptions } from "@/lib/queries/tenant-settings.queries";
import { usePreflightMarkets } from "../hooks/usePreflightMarkets";
import { reportsInSelectedScope } from "../lib/market-filter";
import {
  MarketSelect,
  type MarketScope,
} from "../components/MarketSelect/MarketSelect";
import { ManageMarketsDialog } from "../components/ManageMarketsDialog/ManageMarketsDialog";
import { NewReportDialog } from "../components/NewReportDialog/NewReportDialog";
import { MarketOverview } from "../components/MarketOverview/MarketOverview";
import {
  PastReportsTable,
  type OpenPreflightReportOptions,
} from "../components/PastReportsTable/PastReportsTable";

/** Same creatives, same panel — skip the state write so hovering a dot twice is free. */
function isSameAssetIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

export interface PreFlightHubPageProps {
  /** Navigate to a report's detail route (wired by the route component). */
  onOpenReport: (
    reportId: string,
    options?: OpenPreflightReportOptions,
  ) => void;
}

/** Which of the three no-data states the hub is in, or null when there is data to show. */
type HubEmptyState = "no-markets" | "no-reports" | "scoped-no-reports";

/**
 * The Pre Flight Hub: the market scope lives here and feeds the selector, the aggregate
 * dashboard, and the Past Reports table, so the whole page reads one scope (R8). Report
 * creation happens in a modal opened from the header (R11), and each of the three no-data
 * states replaces the dashboard rather than rendering an empty one (R23–R25).
 */
export function PreFlightHubPage({ onOpenReport }: PreFlightHubPageProps) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((state) => state.activeTenantId);
  const tenantSettingsQuery = useQuery(
    tenantSettingsQueryOptions(activeTenantId),
  );
  const {
    markets,
    reportCount,
    isPending: areMarketsPending,
  } = usePreflightMarkets();

  const [marketScope, setMarketScope] = useState<MarketScope>(null);
  const [platformFilter, setPlatformFilter] = useState<
    PreflightPlatformCode | undefined
  >();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);

  const insightsQuery = useQuery(
    preflightInsightsQueryOptions(activeTenantId, marketScope),
  );

  // The aggregate carries no thumbnails — read URLs expire, so previews are fetched for the
  // creatives the score rail's open panel is showing.
  const [previewAssetIds, setPreviewAssetIds] = useState<readonly string[]>([]);
  const previewsQuery = useQuery(
    preflightCreativePreviewsQueryOptions(activeTenantId, previewAssetIds),
  );
  const previewsByAssetId = useMemo(
    () =>
      previewsQuery.data
        ? Object.fromEntries(
            previewsQuery.data.map(({ assetId, thumbnailUrl, isVideo }) => [
              assetId,
              { thumbnailUrl, isVideo },
            ]),
          )
        : undefined,
    [previewsQuery.data],
  );
  const handlePanelOpen = useCallback((assetIds: readonly string[]) => {
    setPreviewAssetIds((current) =>
      isSameAssetIds(current, assetIds) ? current : [...assetIds],
    );
  }, []);

  const selectedMarkets = (marketScope ?? []).flatMap((id) => {
    const market = markets.find((row) => row.id === id);
    return market ? [market] : [];
  });
  const selectedMarketNames = selectedMarkets.map((market) => market.name).join(", ");
  // Unique live-report count comes with the market list, so empty states do not
  // wait on the aggregate — and a queued report still counts as one.
  const reportsInScope = reportsInSelectedScope(
    markets,
    reportCount,
    marketScope,
  );

  const emptyState: HubEmptyState | null = areMarketsPending
    ? null
    : markets.length === 0
      ? "no-markets"
      : reportsInScope > 0
        ? null
        : marketScope
          ? "scoped-no-reports"
          : "no-reports";

  const hasScopeData = !areMarketsPending && emptyState === null;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 p-4 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("preflight.hub.title")}
          </h1>
          <p className="text-muted-foreground mt-1.5 max-w-xl text-sm">
            {t(
              "preflight.hub.subtitle",
              "Upload creatives or paste asset URLs to score them against platform-specific creative standards.",
            )}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-end gap-2.5">
          <Button iconStart="plus" onClick={() => setIsCreateOpen(true)}>
            {t("preflight.hub.newReport", "New report")}
          </Button>
          <div className="flex flex-col gap-1.5">
            {/* The trigger carries its own scope aria-label, so the eyebrow is decoration. */}
            <span
              aria-hidden
              className="text-muted-foreground font-mono text-[10px] tracking-widest uppercase"
            >
              {t("preflight.markets.scopeLabel", "Market")}
            </span>
            <MarketSelect
              markets={markets}
              allMarketsReportCount={reportCount}
              value={marketScope}
              onChange={setMarketScope}
              size="default"
            />
          </div>
        </div>
      </div>

      {emptyState === "no-markets" ? (
        <EmptyState
          icon="flag"
          title={t("preflight.hub.noMarketsTitle", "No markets yet")}
          description={t(
            "preflight.hub.noMarketsDescription",
            "Add the markets you run creatives in, then score your first report against one of them.",
          )}
          action={
            <Button variant="secondary" onClick={() => setIsManageOpen(true)}>
              {t("preflight.markets.manage", "Manage markets")}
            </Button>
          }
        />
      ) : null}

      {emptyState === "no-reports" ? (
        <EmptyState
          icon="file-text"
          title={t(
            "preflight.hub.firstReportTitle",
            "Score your first creatives",
          )}
          description={t(
            "preflight.hub.firstReportDescription",
            "Create a report to see how your creatives score across every market.",
          )}
          action={
            <Button onClick={() => setIsCreateOpen(true)}>
              {t("preflight.hub.firstReportCta", "Create the first report")}
            </Button>
          }
        />
      ) : null}

      {emptyState === "scoped-no-reports" ? (
        <EmptyState
          icon="file-text"
          title={t(
            "preflight.hub.scopedEmptyTitle",
            "No reports in {{market}}",
            {
              market: selectedMarketNames,
            },
          )}
          description={t(
            "preflight.hub.scopedEmptyDescription",
            "Nothing has been scored in this scope yet.",
          )}
          action={
            <Button variant="secondary" onClick={() => setMarketScope(null)}>
              {t("preflight.hub.allMarketsCta", "View all markets")}
            </Button>
          }
        />
      ) : null}

      {hasScopeData && insightsQuery.data ? (
        <MarketOverview
          insights={insightsQuery.data}
          scopeLabel={
            selectedMarketNames ||
            t(
              "preflight.marketOverview.scopeAllMarkets",
              "all markets combined",
            )
          }
          onSelectMarket={(marketId) => setMarketScope([marketId])}
          onOpenReport={onOpenReport}
          onSelectPlatform={setPlatformFilter}
          previewsByAssetId={previewsByAssetId}
          onPanelOpen={handlePanelOpen}
        />
      ) : null}

      {hasScopeData && !insightsQuery.data && !insightsQuery.isError ? (
        <div
          aria-hidden
          className="bg-muted/40 h-64 animate-pulse rounded-lg"
        />
      ) : null}

      {hasScopeData ? (
        <PastReportsTable
          onOpenReport={onOpenReport}
          market={marketScope}
          platform={platformFilter}
          onPlatformChange={setPlatformFilter}
        />
      ) : null}

      <NewReportDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        markets={markets}
        defaultBrand={tenantSettingsQuery.data?.defaultBrand ?? null}
        isDefaultBrandLoading={tenantSettingsQuery.isLoading}
      />

      {/* The no-markets state needs its own route into the panel, since there is no
          market in the selector to manage from. */}
      <ManageMarketsDialog
        open={isManageOpen}
        onOpenChange={setIsManageOpen}
        markets={markets}
        onMarketRemoved={(marketId) => {
          if (marketScope == null) return;
          const next = marketScope.filter((id) => id !== marketId);
          setMarketScope(next.length === 0 ? null : next);
        }}
      />
    </div>
  );
}
